<template lang="pug">
.timespan-picker
    .timespan-picker__presets(role="group" :aria-label="$t('Timespan')")
        el-button(
            v-for="preset in presets"
            :key="preset.key"
            size="mini"
            :type="preset.key === activePreset ? 'primary' : 'default'"
            @click="apply(preset.key)"
        ) {{ preset.label }}
    //- DAY bounds, shown as days. A monthrange displayed "2026-09 - 2026-09"
    //- for a span that actually runs 01.09. to 30.09., which is both the wrong
    //- format for German and vaguer than the value behind it.
    el-date-picker.timespan-picker__range(
        v-model="spanRange"
        type="daterange"
        size="mini"
        unlink-panels
        :clearable="false"
        :format="dateFormat"
        value-format="yyyy-MM-dd"
        :start-placeholder="$t('Start date')"
        :end-placeholder="$t('End date')"
    )
</template>

<i18n>
{
    "de": {
        "Timespan": "Zeitraum",
        "This month": "Dieser Monat",
        "Last month": "Letzter Monat",
        "Last 3 months": "Letzte 3 Monate",
        "This year": "Dieses Jahr",
        "Start date": "Startdatum",
        "End date": "Enddatum"
    }
}
</i18n>

<script>
import _ from 'lodash';

/**
 * The span every figure on a page is read for.
 *
 * The quick presets are the whole point: "this month" and "last month" are what
 * gets asked, and making somebody pick two dates for them is the slow path. The
 * range picker stays for everything else.
 *
 * `v-model` is `{ from, to }` as inclusive day bounds in `YYYY-MM-DD` — a shape
 * the server can take without knowing which button produced it. Presets are a
 * UI convenience, not a server concept.
 */
const pad = (value) => `${value}`.padStart(2, '0');

const iso = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// The last day of a month, reached by stepping back from the first of the next.
const endOfMonth = (year, month) => new Date(year, month + 1, 0);

export default {
    name: 'TimespanPicker',
    props: {
        value: {
            type: Object,
            default: null,
        },
    },

    computed: {
        presets() {
            return [
                { key: 'current', label: this.$t('This month') },
                { key: 'last', label: this.$t('Last month') },
                { key: 'quarter', label: this.$t('Last 3 months') },
                { key: 'year', label: this.$t('This year') },
            ];
        },
        /**
         * DERIVED from the span, not its own state. It used to be data that
         * every preset blanked, so the picker sat empty while a preset was
         * clearly active - two controls showing one value, one of them lying.
         */
        /**
         * A real v-model, not `:value` + `@change`.
         *
         * Element commits a range through its own input event; with the value
         * bound one-way the pick was written to the picker's internal state and
         * then immediately overwritten by the unchanged prop, so choosing dates
         * by hand did nothing at all.
         */
        spanRange: {
            get() {
                const from = _.get(this.value, 'from');
                const to = _.get(this.value, 'to');

                if (!from || !to) return null;

                return [from, to];
            },
            set(range) {
                this.applyRange(range);
            },
        },
        // The same pairing the ticket forms use: German reading order on screen,
        // ISO in the value.
        dateFormat() {
            return this.$i18n.locale === 'de' ? 'dd.MM.yyyy' : 'yyyy-MM-dd';
        },
        /**
         * DERIVED, never stored: whichever preset produces exactly the span
         * being shown IS the active one.
         *
         * This used to be a data flag, and it went stale the moment a second
         * screen mounted. The span is shared between the team screens, so
         * opening the list and then a person found a value already in the
         * store, took the "not mine" branch and blanked the flag - leaving
         * every chip grey while "Dieser Monat" was in fact selected.
         */
        activePreset() {
            const from = _.get(this.value, 'from');
            const to = _.get(this.value, 'to');

            if (!from || !to) return null;

            const match = this.presets.find((preset) => {
                const span = this.spanFor(preset.key);

                return span.from === from && span.to === to;
            });

            return match ? match.key : null;
        },
    },
    created() {
        // A page opens on the month it is being read in.
        if (!this.value) this.apply('current');
    },
    methods: {
        spanFor(key) {
            const today = new Date();
            const year = today.getFullYear();
            const month = today.getMonth();

            if (key === 'last') {
                return {
                    from: iso(new Date(year, month - 1, 1)),
                    to: iso(endOfMonth(year, month - 1)),
                };
            }

            if (key === 'quarter') {
                return {
                    from: iso(new Date(year, month - 2, 1)),
                    to: iso(endOfMonth(year, month)),
                };
            }

            if (key === 'year') {
                return {
                    from: iso(new Date(year, 0, 1)),
                    to: iso(endOfMonth(year, 11)),
                };
            }

            return {
                from: iso(new Date(year, month, 1)),
                to: iso(endOfMonth(year, month)),
            };
        },
        apply(key) {
            this.$emit('input', this.spanFor(key));
        },
        applyRange(range) {
            if (!range || !range[0] || !range[1]) return;

            // `value-format` already hands back `YYYY-MM-DD`, and the days are
            // now explicit, so there is no month end left to guess at.
            this.$emit('input', { from: range[0], to: range[1] });
        },
    },
};
</script>

<style lang="sass" scoped>
.timespan-picker
    display: flex
    flex-wrap: wrap
    align-items: center
    gap: 0.375rem

    // Element gives adjacent buttons their own margin, which stacks on top of
    // the gap above — gap alone should decide the spacing.
    .el-button + .el-button
        margin-left: 0

.timespan-picker__presets
    display: flex
    flex-wrap: wrap
    gap: 0.375rem

.timespan-picker__range
    margin-left: auto
    width: 260px
</style>
